import { Controller } from "@hotwired/stimulus"
import { parseSettingsParams, selectedSampleId } from "../helpers/parse_settings"

const STALE_STATUSES = [401, 403, 409]
const CARD_SELECTOR = "[data-controller~='pending-rows']"

export default class extends Controller {
  static targets = ["error", "errorMessage", "result", "resultEmpty", "mode", "reorder", "reorderList"]
  static values = {
    url: String,
    formId: String,
    sampleSelectId: String,
    previewSelectId: String,
    nothingToAppend: String,
    stale: String,
    noSampleChosen: String,
    typeLabels: Object,
    recognized: String,
    ambiguous: String,
    useAlternative: String,
    examplesLabel: String,
    alternativeClass: String,
    mappedPositions: Object,
    reorderCopy: Object,
  }

  open() {
    this.clearError()
    this.columns = []
    this.syncSampleFromPreview()
    this.dialog()?.open()
    this.sampleSelect()?.focus()
    this.read()
  }

  syncSampleFromPreview() {
    const select = this.sampleSelect()
    const previewId = selectedSampleId(this.previewSelectIdValue)
    if (!select || !previewId) return
    if (Array.from(select.options).some((option) => option.value === previewId)) select.value = previewId
  }

  async read() {
    this.clearError()
    this.columns = []
    this.renderResult()

    const sampleId = this.sampleSelect()?.value
    if (!sampleId) return this.showError(this.noSampleChosenValue)

    const { columns, error } = await this.fetchColumns(sampleId)
    this.columns = columns
    this.renderResult()
    if (!columns.length) this.showError(error)
  }

  apply() {
    if (!this.columns?.length) return this.showError(this.noSampleChosenValue)

    const pendingRows = this.pendingRows()
    if (!pendingRows) return

    if (this.mode() === "replace") pendingRows.discardAll()

    const taken = this.configuredPositions()
    let added = 0
    this.columns.forEach((column) => {
      if (taken.has(column.position)) return

      this.fill(pendingRows.addRow(), column)
      added += 1
    })
    if (added === 0) return this.showError(this.nothingToAppendValue)

    this.markDirty()
    this.dialog()?.close()
  }

  useAlternative(event) {
    const column = this.columns?.[Number(event.params.index)]
    if (!column || !column.alternative_format) return

    const chosen = column.data_format
    column.data_format = column.alternative_format
    column.alternative_format = chosen
    this.renderResult()
  }

  mode() {
    return this.hasModeTarget ? this.modeTarget.value : "replace"
  }

  renderResult() {
    if (this.hasResultTarget) {
      this.resultTarget.replaceChildren(
        ...this.columns.map((column, index) => this.resultRow(column, index)),
      )
    }
    if (this.hasResultEmptyTarget) this.resultEmptyTarget.hidden = this.columns.length > 0
    this.renderWarning()
  }

  renderWarning() {
    if (!this.hasReorderTarget) return

    const lines = this.changedCells()
    this.reorderTarget.hidden = lines.length === 0
    if (!this.hasReorderListTarget) return

    this.reorderListTarget.replaceChildren(...lines.map((line) => this.reorderItem(line)))
  }

  changedCells() {
    if (this.mode() !== "replace") return []

    const held = this.currentNames()
    return (this.columns || [])
      .filter((column) => this.changesIdentity(column, held))
      .map((column) => this.changedCellText(column, held.get(column.position)))
  }

  changesIdentity(column, held) {
    const name = held.get(column.position)
    if (name === undefined || !this.readerCount(column.position)) return false

    return this.identity(name) !== this.identity(column.name)
  }

  identity(name) {
    return String(name ?? "").trim().replace(/\s+/g, " ").toLowerCase()
  }

  readerCount(position) {
    return Number(this.mappedPositionsValue?.[position] || 0)
  }

  changedCellText(column, name) {
    return this.reorderCopyValue.line
      .replace("%{position}", column.position)
      .replace("%{current}", name)
      .replace("%{incoming}", column.name)
      .replace("%{mappings}", this.readersText(this.readerCount(column.position)))
  }

  readersText(count) {
    if (count === 1) return this.reorderCopyValue.one

    return this.reorderCopyValue.other.replace("%{count}", count)
  }

  reorderItem(text) {
    const item = document.createElement("li")
    item.dataset.testId = "column-import-reorder-line"
    item.textContent = text
    return item
  }

  resultRow(column, index) {
    const row = document.createElement("tr")
    row.dataset.testId = "column-import-result-row"
    row.append(
      this.cell(column.position, "py-1 pr-2 align-top text-zinc-500 dark:text-zinc-400"),
      this.nameCell(column),
      this.cell(this.typeLabel(column), "py-1 pr-2 align-top text-zinc-700 dark:text-zinc-300", "column-import-result-type"),
      this.cell(column.data_format || "", "py-1 pr-2 align-top text-zinc-700 dark:text-zinc-300", "column-import-result-format"),
      this.detectionCell(column, index),
    )
    return row
  }

  nameCell(column) {
    const td = this.cell(
      column.name,
      "py-1 pr-2 align-top text-zinc-900 dark:text-zinc-100",
      "column-import-result-name",
    )
    const examples = Array.isArray(column.examples) ? column.examples : []
    if (examples.length) td.append(this.examplesLine(examples))

    return td
  }

  examplesLine(examples) {
    const line = document.createElement("div")
    line.className = "mt-0.5 break-words text-xs text-zinc-500 dark:text-zinc-400"
    line.dataset.testId = "column-import-result-examples"
    line.title = this.examplesLabelValue
    line.textContent = examples.join(" \u00b7 ")
    return line
  }

  cell(text, className, testId) {
    const td = document.createElement("td")
    td.className = className
    td.textContent = text
    if (testId) td.dataset.testId = testId
    return td
  }

  detectionCell(column, index) {
    const td = this.cell(this.recognizedText(column), "py-1 align-top text-zinc-500 dark:text-zinc-400")
    if (!column.alternative_format) return td

    const flagRow = document.createElement("div")
    flagRow.className = "mt-1"
    const flag = document.createElement("span")
    flag.className =
      "whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900 dark:text-amber-100"
    flag.dataset.testId = "column-import-result-ambiguous"
    flag.textContent = this.ambiguousValue
    flagRow.append(flag)

    const buttonRow = document.createElement("div")
    buttonRow.className = "mt-1"
    const button = document.createElement("button")
    button.type = "button"
    button.className = this.alternativeClassValue
    button.dataset.testId = "column-import-use-alternative"
    button.dataset.action = "import-columns#useAlternative"
    button.dataset.importColumnsIndexParam = index
    button.textContent = this.useAlternativeValue.replace("%{data_format}", column.alternative_format)
    buttonRow.append(button)

    td.append(flagRow, buttonRow)
    return td
  }

  recognizedText(column) {
    return this.recognizedValue
      .replace("%{matched}", column.matched)
      .replace("%{total}", column.total)
  }

  typeLabel(column) {
    return this.typeLabelsValue[column.data_type] || column.data_type
  }

  fill(row, column) {
    const name = row.querySelector("[data-import-columns-name]")
    if (name) name.value = column.name

    const position = row.querySelector("[data-column-position-field]")
    if (position) position.value = column.position
    row.dataset.columnPosition = column.position

    const label = row.querySelector("[data-column-position-label]")
    if (label) label.textContent = column.position

    const type = row.querySelector("[data-import-columns-type]")
    if (type) type.value = column.data_type

    this.fillFormat(row, column)
    this.application.getControllerForElementAndIdentifier(row, "column-format")?.refresh()
  }

  fillFormat(row, column) {
    const format = row.querySelector("[data-import-columns-format]")
    if (!format || !column.data_format) return
    if (!Array.from(format.options).some((option) => option.value === column.data_format)) return

    format.value = column.data_format
  }

  configuredPositions() {
    return new Set(this.currentNames().keys())
  }

  currentNames() {
    const card = this.card()
    if (!card) return new Map()

    const rows = Array.from(card.querySelectorAll("[data-pending-rows-row]"))
    return new Map(
      rows
        .filter((row) => !row.hidden && row.dataset.columnPosition)
        .map((row) => [Number(row.dataset.columnPosition), this.nameOf(row)]),
    )
  }

  nameOf(row) {
    return row.querySelector("[data-import-columns-name]")?.value ?? ""
  }

  async fetchColumns(sampleId) {
    const requestHeaders = {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    }
    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (token) requestHeaders["X-CSRF-Token"] = token

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: requestHeaders,
        body: this.requestBody(sampleId).toString(),
      })
      if (!response.ok) {
        return { columns: [], error: STALE_STATUSES.includes(response.status) ? this.staleValue : null }
      }
      const payload = await response.json()
      return {
        columns: Array.isArray(payload.columns) ? payload.columns : [],
        error: payload.error || null,
      }
    } catch {
      return { columns: [], error: null }
    }
  }

  requestBody(sampleId) {
    const params = parseSettingsParams(this.formIdValue)
    params.set("sample_id", sampleId)
    return params
  }

  sampleSelect() {
    return this.sampleSelectIdValue ? document.getElementById(this.sampleSelectIdValue) : null
  }

  card() {
    return document.querySelector(CARD_SELECTOR)
  }

  pendingRows() {
    const card = this.card()
    if (!card) return null

    return this.application.getControllerForElementAndIdentifier(card, "pending-rows")
  }

  dialog() {
    return this.application.getControllerForElementAndIdentifier(this.element, "dialog")
  }

  showError(message) {
    if (!this.hasErrorTarget || !this.hasErrorMessageTarget) return

    const fallback = this.errorMessageTarget.dataset.fallback || ""
    this.errorMessageTarget.textContent = message || fallback
    this.errorTarget.hidden = false
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  markDirty() {
    const host = this.card()?.closest("[data-controller~='dirty-guard']")
    if (!host) return

    this.application.getControllerForElementAndIdentifier(host, "dirty-guard")?.markDirty()
  }
}
